import React, { useState, useEffect } from 'react';
import { fetchCategoryGroups } from '../../../../api/masters/styles/categoryGroup/categoryGroupApi';
import { importCategoryGroupCSV } from '../../../../api/masters/styles/categoryGroup/categoryGroupApi';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Papa from 'papaparse';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useSelector } from 'react-redux';


const CategoryGroupForm = ({ onSubmit, editingCategoryGroup, onCancel, formSuccess = '', formError = '', setFormSuccess, setFormError, users = [], companies = [] }) => {
  const [form, setForm] = useState({ category_group_code: '', category_group_name: '', status: 'Active', company_code: '' });
  const [focus, setFocus] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codeChecking, setCodeChecking] = useState(false);
  const [codeExists, setCodeExists] = useState(false);

  // Redux: get all category groups for template download
  const categoryGroups = useSelector(state => state.categoryGroup.list || []);
  // State for import feature
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importedRows, setImportedRows] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importSuccess, setImportSuccess] = useState('');
  const [importError, setImportError] = useState('');
  const [importFile, setImportFile] = useState(null);

  useEffect(() => {
    if (editingCategoryGroup) setForm(editingCategoryGroup);
    else setForm({ category_group_code: '', category_group_name: '', status: 'Active', company_code: '' });
    setErrors({});
    setCodeExists(false);
    if (setFormSuccess) setFormSuccess('');
    if (setFormError) setFormError('');
  }, [editingCategoryGroup]);

  useEffect(() => {
    if (formSuccess) {
      const timer = setTimeout(() => {
        setFormSuccess && setFormSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [formSuccess, setFormSuccess]);

  // Auto-clear import success/error messages after 3 seconds
  useEffect(() => {
    if (importSuccess || importError) {
      const timer = setTimeout(() => {
        setImportSuccess('');
        setImportError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [importSuccess, importError]);

  const checkCodeExists = async (code) => {
    setCodeChecking(true);
    try {
      const groups = await fetchCategoryGroups();
      const exists = groups.some(
        (g) => g.category_group_code === code && (!editingCategoryGroup || g.category_group_id !== editingCategoryGroup.category_group_id)
      );
      setCodeExists(exists);
      if (exists) {
        setErrors((prev) => ({ ...prev, category_group_code: 'This code already exists and cannot be reused.' }));
      } else {
        setErrors((prev) => ({ ...prev, category_group_code: undefined }));
      }
      return exists;
    } catch (e) {
      setCodeExists(false);
      setErrors((prev) => ({ ...prev, category_group_code: undefined }));
      return false;
    } finally {
      setCodeChecking(false);
    }
  };

  // Helper function for alphanumeric check
  const isAlphanumeric = (str) => /^[A-Za-z0-9]+$/.test(str);

  const validate = async () => {
    const newErrors = {};
    const code = form.category_group_code.trim();
    if (!code) newErrors.category_group_code = 'Please enter a code.';
    else if (/[^A-Za-z0-9]/.test(code)) newErrors.category_group_code = 'Code can only contain letters and numbers.';
    if (!form.category_group_name.trim()) newErrors.category_group_name = 'Please enter a group name.';
    if (!form.company_code) newErrors.company_code = 'Please select a company code.';
    if (code && isAlphanumeric(code)) {
      const exists = await checkCodeExists(code);
      if (exists) newErrors.category_group_code = 'This code already exists and cannot be reused.';
    }
    setErrors(newErrors);
    if (setFormError) setFormError('');
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    if (name === 'category_group_code') {
      // Only show error if value is non-empty and contains any non-alphanumeric character
      if (value && /[^A-Za-z0-9]/.test(value)) {
        setErrors(prev => ({ ...prev, [name]: 'Code can only contain letters and numbers.' }));
      } else {
        setErrors(prev => ({ ...prev, [name]: undefined }));
      }
    } else {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }

    setCodeExists(false);
    if (setFormError) setFormError('');
    if (setFormSuccess) setFormSuccess('');
  };

  const handleFocus = e => setFocus({ ...focus, [e.target.name]: true });

  const handleBlur = async (e) => {
    setFocus({ ...focus, [e.target.name]: false });
    if (e.target.name === 'category_group_code') {
      const value = form.category_group_code.trim();
      if (!value) {
        setErrors((prev) => ({ ...prev, category_group_code: 'Please enter a code.' }));
        return;
      }
      if (/[^A-Za-z0-9]/.test(value)) {
        setErrors((prev) => ({ ...prev, category_group_code: 'Code can only contain letters and numbers.' }));
        return;
      }
      await checkCodeExists(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const isValid = await validate();
    if (!isValid) {
      setIsSubmitting(false);
      return;
    }
    setErrors({});
    try {
      await onSubmit({ ...form, company_code: form.company_code });
      if (setFormSuccess)
        setFormSuccess(editingCategoryGroup ? 'Category Group updated successfully!' : 'Category Group added successfully!');
      if (setFormError) setFormError('');
      if (!editingCategoryGroup) {
        setForm({ category_group_code: '', category_group_name: '', status: 'Active', company_code: '' });
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;
      if (
        err?.response?.status === 409 ||
        (message && message.toLowerCase().includes('already exists'))
      ) {
        setErrors((prev) => ({
          ...prev,
          category_group_code: 'This code already exists and cannot be reused.',
        }));
        if (setFormSuccess) setFormSuccess('');
        if (setFormError) setFormError('');
        return;
      }
      if (setFormError) setFormError(message || 'An error occurred.');
      if (setFormSuccess) setFormSuccess('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setForm({ category_group_code: '', category_group_name: '', status: 'Active', company_code: '' });
    setErrors({});
    setCodeExists(false);
    setFormSuccess && setFormSuccess('');
    setFormError && setFormError('');
  };

  // Download Template: generate CSV from current data
  const handleDownloadTemplate = () => {
    const headers = ['category_group_code','category_group_name','company_code','status'];
    const csv = Papa.unparse([headers]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'category_group_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Import CSV: upload file to backend and import
  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const required = ['category_group_code','category_group_name','company_code','status'];
        const missing = required.filter(f => !results.meta.fields.includes(f));
        if (missing.length) {
          setImportError('Missing required fields: ' + missing.join(', '));
          setImportDialogOpen(true);
          return;
        }
        setImportedRows(results.data);
        setImportFile(file);
        setImportDialogOpen(true);
        setImportError('');
      },
      error: (err) => {
        setImportError('CSV parse error: ' + err.message);
        setImportDialogOpen(true);
      }
    });
  };

  // Confirm import: call onSubmit for each record
  const handleConfirmImport = async () => {
    if (!importFile) return;
    setImportSuccess('');
    setImportError('');
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await importCategoryGroupCSV(formData);
      const { imported, failed, errors } = response;
      setImportSuccess(`Imported ${imported} record(s).${failed ? ` ${failed} failed.` : ''}`);
      setImportErrors(errors || []);
      setImportedRows([]);
      setImportFile(null);
    } catch (err) {
      setImportError('Upload/import failed: ' + (err.response?.data?.error || err.message));
    }
  };

  // Cancel import
  const handleCancelImport = () => {
    setImportDialogOpen(false);
    setImportedRows([]);
    setImportErrors([]);
    setImportFile(null);
  };

  // Helper to format date
  const formatDate = (date) => date ? dayjs(date).format('DD-MM-YYYY hh:mm A') : 'Unknown';
  
  // Helper to get user display name and role (if users list is available)
  const getUserDisplay = (userId) => {
    if (!userId) return 'Unknown';
    const user = users.find(u => u.user_id === userId);
    if (!user) return 'Unknown';
    return `${user.username} (${user.role_name || 'Unknown'})`;
  };

  // Prepare options for select fields
  const companyOptions = companies
    .filter(c => c.trash === 0)
    .map(company => ({
      value: company.company_code,
      label: `${company.company_code} - ${company.company_name}`
    }));

  const statusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' }
  ];

  return (
    <form onSubmit={handleSubmit} autoComplete="off">
      {/* Download Template and Import CSV Buttons - compact, green, inline */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: 'flex-start' }}>
        <Button
          variant="contained"
          startIcon={<CloudDownloadIcon />}
          onClick={handleDownloadTemplate}
          sx={{
            bgcolor: '#8e24aa',
            '&:hover': { bgcolor: '#6a1b9a' },
            fontWeight: 700,
            textTransform: 'uppercase',
            borderRadius: 2,
            minWidth: 110,
            height: 36,
            fontSize: 12,
            letterSpacing: 1,
            boxShadow: 'none',
            px: 1.5,
          }}
        >
          Download Template
        </Button>
        <Button
          variant="contained"
          component="label"
          startIcon={<UploadFileIcon />}
          sx={{
            bgcolor: '#00bcd4',
            '&:hover': { bgcolor: '#008ba3' },
            fontWeight: 700,
            textTransform: 'uppercase',
            borderRadius: 2,
            minWidth: 110,
            height: 36,
            fontSize: 12,
            letterSpacing: 1,
            boxShadow: 'none',
            px: 1.5,
          }}
        >
          Import CSV
          <input
            type="file"
            accept=".csv"
            hidden
            onChange={handleImportCSV}
          />
        </Button>
      </Stack>
      {/* Show import success/error messages */}
      {importSuccess && <Alert severity="success" sx={{ mb: 2 }}>{importSuccess}</Alert>}
      {importError && <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>}
      {/* Confirm dialog for import */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Category Groups</DialogTitle>
        <DialogContent>
          {importError && <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>}
          {importSuccess && <Alert severity="success" sx={{ mb: 2 }}>{importSuccess}</Alert>}
          {importedRows.length > 0 && (
            <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, fontSize: 16, borderBottom: '2px solid #e0e0e0', background: '#f8f9fa' }}>category_group_code</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, fontSize: 16, borderBottom: '2px solid #e0e0e0', background: '#f8f9fa' }}>category_group_name</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, fontSize: 16, borderBottom: '2px solid #e0e0e0', background: '#f8f9fa' }}>company_code</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, fontSize: 16, borderBottom: '2px solid #e0e0e0', background: '#f8f9fa' }}>status</th>
                  </tr>
                </thead>
                <tbody>
                  {importedRows.map((row, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ textAlign: 'left', padding: '8px 12px', fontSize: 15, color: '#222' }}>{row.category_group_code}</td>
                      <td style={{ textAlign: 'left', padding: '8px 12px', fontSize: 15, color: '#222' }}>{row.category_group_name}</td>
                      <td style={{ textAlign: 'left', padding: '8px 12px', fontSize: 15, color: '#222' }}>{row.company_code}</td>
                      <td style={{ textAlign: 'left', padding: '8px 12px', fontSize: 15, color: '#222' }}>{row.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
          {importErrors.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">Some rows failed to import:</Alert>
              <ul>
                {importErrors.map((err, idx) => (
                  <li key={idx}>{err.error} (Row: {JSON.stringify(err.row)})</li>
                ))}
              </ul>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', p: 2 }}>
          <Button
            onClick={() => setImportDialogOpen(false)}
            variant="contained"
            sx={{
              bgcolor: '#757575',
              color: '#fff',
              fontWeight: 700,
              borderRadius: 2,
              minWidth: 110,
              height: 38,
              fontSize: 12,
              letterSpacing: 1,
              boxShadow: 'none',
              mr: 1.5,
              '&:hover': { bgcolor: '#424242' },
            }}
          >
            Cancel
          </Button>
          {!importSuccess && !importError && importedRows.length > 0 && (
            <Button
              onClick={handleConfirmImport}
              variant="contained"
              sx={{
                bgcolor: '#43a047',
                color: '#fff',
                fontWeight: 700,
                borderRadius: 2,
                minWidth: 110,
                height: 38,
                fontSize: 12,
                letterSpacing: 1,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#2e7d32' },
              }}
            >
              Confirm Import
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Box sx={{ fontWeight: 700, fontSize: 18, color: '#111', mb: 2.5, letterSpacing: 0.7, textAlign: 'left', width: '100%' }}>
        {editingCategoryGroup ? 'Edit Category Group' : 'Add Category Group'}
      </Box>
      {formError && <Box sx={{ color: 'red', fontSize: 15, mb: 1 }}>{formError}</Box>}
      {formSuccess && !Object.values(errors).some(Boolean) && (
        <Box sx={{ color: '#00C853', fontSize: 15, fontWeight: 500, mb: 1 }}>{formSuccess}</Box>
      )}
      <Stack spacing={2} alignItems="center">
        <FormControl fullWidth error={!!errors.company_code} size="small">
          {/* Autocomplete for company: search by code or name, label as CMP001 - Mindtree */}
          <Autocomplete
        id="company_code"
            options={companies.filter(c => c.trash === 0)}
            getOptionLabel={(option) => option.company_code && option.company_name ? `${option.company_code} - ${option.company_name}` : ''}
            value={companies.find(c => c.company_code === form.company_code) || null}
            onChange={(event, newValue) => {
              setForm({ ...form, company_code: newValue ? newValue.company_code : '' });
              setErrors(prev => ({ ...prev, company_code: undefined }));
              if (setFormError) setFormError('');
              if (setFormSuccess) setFormSuccess('');
            }}
            isOptionEqualToValue={(option, value) => option.company_code === value.company_code}
            renderInput={(params) => (
              <TextField
                {...params}
            label="Company Code"
                error={!!errors.company_code}
                helperText={errors.company_code}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                minHeight: 45,
                height: 45,
                color: '#111',
                    '& fieldset': { borderColor: !!errors.company_code ? '#D32F2F' : '#BDBDBD' },
                    '&:hover fieldset': { borderColor: !!errors.company_code ? '#D32F2F' : '#BDBDBD' },
                    '&.Mui-focused fieldset': { borderColor: !!errors.company_code ? '#D32F2F' : '#00C853' },
                  },
                  '& label.Mui-focused': { color: '#00C853' },
                }}
                inputProps={{ ...params.inputProps, maxLength: 50 }}
              />
            )}
            filterOptions={(options, { inputValue }) =>
              options.filter(option => {
                const search = inputValue.toLowerCase();
                return (
                  (option.company_name && option.company_name.toLowerCase().includes(search)) ||
                  (option.company_code && option.company_code.toLowerCase().includes(search))
                );
              })
            }
            clearOnEscape
          />
        </FormControl>
      <TextField
          label="CategoryGroup Code*"
        name="category_group_code"
        value={form.category_group_code}
        onChange={handleChange}
        onBlur={handleBlur}
          error={!!errors.category_group_code}
          helperText={errors.category_group_code}
          fullWidth
          inputProps={{ maxLength: 20 }}
          disabled={!!editingCategoryGroup || codeChecking}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: '#111',
              minHeight: 45,
              height: 45,
              '& fieldset': { borderColor: !!errors.category_group_code ? '#D32F2F' : '#BDBDBD' },
              '&:hover fieldset': { borderColor: !!errors.category_group_code ? '#D32F2F' : '#BDBDBD' },
              '&.Mui-focused fieldset': { borderColor: !!errors.category_group_code ? '#D32F2F' : '#00C853' },
              background: '#fff',
            },
            mb: 1.5,
            '& label.Mui-focused': { color: '#00C853' },
          }}
      />
        {codeChecking && <Box sx={{ color: '#00C853', fontSize: 12 }}>Checking...</Box>}
      <TextField
          label="CategoryGroup Name*"
        name="category_group_name"
        value={form.category_group_name}
        onChange={handleChange}
          error={!!errors.category_group_name}
          helperText={errors.category_group_name}
          fullWidth
          inputProps={{ maxLength: 50 }}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: '#111',
              minHeight: 45,
              height: 45,
              '& fieldset': { borderColor: !!errors.category_group_name ? '#D32F2F' : '#BDBDBD' },
              '&:hover fieldset': { borderColor: !!errors.category_group_name ? '#D32F2F' : '#BDBDBD' },
              '&.Mui-focused fieldset': { borderColor: !!errors.category_group_name ? '#D32F2F' : '#00C853' },
              background: '#fff',
            },
            mb: 1.5,
            '& label.Mui-focused': { color: '#00C853' },
          }}
        />
        <FormControl fullWidth size="small">
          <InputLabel id="status-label" sx={{ color: '#111', '&.Mui-focused': { color: '#00C853' } }}>Status</InputLabel>
          <Select
            labelId="status-label"
        id="status"
        name="status"
            value={form.status}
        label="Status"
        onChange={handleChange}
            variant="outlined"
            size="small"
            sx={{
              minHeight: 45,
              height: 45,
              '& .MuiOutlinedInput-root': {
                minHeight: 45,
                height: 45,
                color: '#111',
                background: '#fff',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00C853 !important' },
              },
            }}
          >
            {statusOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      {(editingCategoryGroup || form.created_by || form.created_at) && (
          <Box sx={{ mt: 2, mb: 1, background: '#f8f9f6', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', p: 2, color: '#222', fontSize: 14, width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#4caf50', marginRight: 6 }}>●</span>
            <span style={{ fontWeight: 600, marginRight: 6 }}>Created by:</span>
            <span style={{ marginRight: 12 }}>{getUserDisplay(editingCategoryGroup?.created_by || form.created_by)}</span>
            <span style={{ color: '#2196f3', fontSize: 13, marginRight: 3 }}>⏰</span>
            <span>{formatDate(editingCategoryGroup?.created_at || form.created_at)}</span>
          </div>
          {editingCategoryGroup && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ color: '#ff9800', marginRight: 6 }}>●</span>
              <span style={{ fontWeight: 600, marginRight: 6 }}>Last updated by:</span>
              <span style={{ marginRight: 12 }}>{getUserDisplay(editingCategoryGroup.updated_by)}</span>
              <span style={{ color: '#2196f3', fontSize: 13, marginRight: 3 }}>⏰</span>
              <span>{formatDate(editingCategoryGroup.updated_at)}</span>
            </div>
          )}
          </Box>
        )}
        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          <Button type="submit" fullWidth variant="contained" sx={{ bgcolor: '#00C853', '&:hover': { bgcolor: '#009e46' }, minWidth: 90, height: 40, fontWeight: 700, fontSize: 16, letterSpacing: 1 }} disabled={isSubmitting || codeExists}>
            {editingCategoryGroup ? 'Update' : 'Add'}
          </Button>
          <Button type="button" fullWidth variant="outlined" sx={{ color: '#00C853', borderColor: '#00C853', minWidth: 90, height: 40, fontWeight: 700, fontSize: 16, letterSpacing: 1, '&:hover, &:active': { borderColor: '#009e46', color: '#009e46' } }} onClick={handleClear} disabled={isSubmitting}>
            Clear
          </Button>
          {editingCategoryGroup && (
            <Button type="button" fullWidth variant="outlined" sx={{ color: '#00C853', borderColor: '#00C853', minWidth: 90, height: 40, fontWeight: 700, fontSize: 16, letterSpacing: 1, '&:hover, &:active': { borderColor: '#009e46', color: '#009e46' } }} onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );
};

export default CategoryGroupForm;
